import axios from 'axios';

// Token-storage helpers: JWT wordt opgeslagen in localStorage zodat we Authorization: Bearer
// kunnen sturen. Dit omzeilt het probleem dat de Kubernetes-ingress de CORS-header naar
// `Allow-Origin: *` herschrijft, waardoor Chrome credentialed requests (cookies) weigert.
const TOKEN_KEY = 'emergent_billing_token';
const REFRESH_KEY = 'emergent_billing_refresh';
export const getToken = () => {
  try { return localStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; }
};
export const getRefreshToken = () => {
  try { return localStorage.getItem(REFRESH_KEY) || ''; } catch { return ''; }
};
// setToken(access, refresh): refresh enkel aanpassen wanneer meegegeven (undefined = ongemoeid laten)
export const setToken = (tk, rt) => {
  try {
    if (tk) localStorage.setItem(TOKEN_KEY, tk);
    else localStorage.removeItem(TOKEN_KEY);
    if (rt !== undefined) {
      if (rt) localStorage.setItem(REFRESH_KEY, rt);
      else localStorage.removeItem(REFRESH_KEY);
    }
  } catch { /* quota/privacy */ }
  // Andere gebruiker/sessie → nooit gecachte of hangende antwoorden van de vorige sessie hergebruiken
  _cache.clear(); _inflight.clear();
};
export const clearToken = () => setToken('', '');

// Vervaltijd (ms) uit de JWT-payload, 0 als onleesbaar
const tokenExp = (tk) => {
  try { return (JSON.parse(atob(tk.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).exp || 0) * 1000; } catch { return 0; }
};

// Sessie stil vernieuwen met het refresh-token (gedeeld tussen gelijktijdige aanroepen).
// Bij een definitief geweigerd refresh-token wordt het verwijderd zodat we niet blijven proberen.
let _refreshing = null;
export const refreshSession = () => {
  if (_refreshing) return _refreshing;
  const rt = getRefreshToken();
  if (!rt) return Promise.reject(new Error('geen refresh token'));
  _refreshing = axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/refresh`, { refresh_token: rt }, { withCredentials: true, timeout: 15000 })
    .then(r => { setToken(r.data.access_token, r.data.refresh_token || rt); return r.data.access_token; })
    .catch(e => { if (e?.response?.status === 401 || e?.response?.status === 403) setToken(getToken(), ''); throw e; })
    .finally(() => { _refreshing = null; });
  return _refreshing;
};
const sessieVerlopen = () => {
  clearToken();
  window.dispatchEvent(new Event('billfac:sessie-verlopen'));
};

const API = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true, // Cookies blijven 2de kanaal voor oude sessies
});

// ─── Performance: identieke GET-requests delen 1 netwerkcall; zware "globale" endpoints worden
// enkele seconden gecachet. Elke niet-GET (opslaan/verwijderen) wist de cache zodat niets veroudert.
const CACHE_TTL_MS = {
  '/instellingen': 20000, '/dashboard': 20000, '/onboarding/status': 20000, '/tenant/me': 20000,
  '/bedrijven': 20000, '/auth/actief-bedrijf': 20000, '/auth/me': 10000, '/verkopers': 10000, '/chauffeurs': 10000,
  '/shop-bestellingen/badge': 10000, '/notificaties/count': 10000, '/mail/counts': 10000,
};
const GET_TIMEOUT_MS = 45000; // een hangende GET mag nooit eindeloos "Laden..." veroorzaken
const _inflight = new Map();
const _cache = new Map();
const _baseAdapter = axios.getAdapter(axios.defaults.adapter);
const _withConfig = (resp, config) => ({ ...resp, config });
export const invalidateApiCache = () => _cache.clear();

API.defaults.adapter = (config) => {
  const method = (config.method || 'get').toLowerCase();
  if (method !== 'get') { _cache.clear(); return _baseAdapter(config); }
  if ((config.responseType && config.responseType !== 'json') || config.noCache) return _baseAdapter(config);
  if (!config.timeout) config.timeout = GET_TIMEOUT_MS;
  const path = (config.url || '').split('?')[0];
  const key = `${config.url}|${JSON.stringify(config.params || {})}`;
  const ttl = CACHE_TTL_MS[path] || 0;
  const hit = ttl && _cache.get(key);
  if (hit && Date.now() - hit.t < ttl) return Promise.resolve(_withConfig(hit.resp, config));
  if (_inflight.has(key)) return _inflight.get(key).then(r => _withConfig(r, config));
  const p = _baseAdapter(config)
    .then(resp => { if (ttl && resp.status === 200) _cache.set(key, { t: Date.now(), resp }); return resp; })
    .finally(() => _inflight.delete(key));
  _inflight.set(key, p);
  return p.then(r => _withConfig(r, config));
};

// Inject Authorization-header bij elke request; vernieuw het access-token proactief als het
// binnen 30 min verloopt (zo loopt een geopende app op gsm/tablet nooit stil op een verlopen token).
API.interceptors.request.use(async (config) => {
  let tk = getToken();
  if (tk && getRefreshToken() && !(config.url || '').startsWith('/auth/')) {
    const exp = tokenExp(tk);
    if (exp && exp - Date.now() < 30 * 60 * 1000) {
      try { tk = await refreshSession(); } catch { /* 401-pad hieronder vangt dit op */ }
    }
  }
  if (tk) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) config.headers.Authorization = `Bearer ${tk}`;
  }
  return config;
});

// Response-interceptor: 401 door verlopen token → 1x stil vernieuwen en het verzoek herhalen.
// Lukt dat niet (refresh-token weg/ongeldig) → sessie beëindigen zodat de app naar het inlogscherm gaat
// i.p.v. te blijven hangen op "Laden...".
const AUTH_PATHS = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/logout'];
API.interceptors.response.use(
  (resp) => resp,
  async (err) => {
    const cfg = err?.config;
    const url = cfg?.url || '';
    if (err?.response?.status !== 401 || !cfg || AUTH_PATHS.some(p => url.startsWith(p))) return Promise.reject(err);
    if (!cfg._retry && getRefreshToken()) {
      cfg._retry = true;
      try {
        const tk = await refreshSession();
        cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${tk}` };
        return API.request(cfg);
      } catch (e2) {
        if (e2?.response?.status === 401 || e2?.response?.status === 403) sessieVerlopen();
        return Promise.reject(err);
      }
    }
    // Geen refresh-token (oude sessie) en het access-token is effectief verlopen → uitloggen
    const tk = getToken();
    if (tk && tokenExp(tk) && tokenExp(tk) < Date.now()) sessieVerlopen();
    return Promise.reject(err);
  }
);

export default API;

export const fmt = (n) =>
  new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(n || 0);

export const today = () => new Date().toISOString().split('T')[0];

export const indDays = (d) => {
  const dt = new Date();
  dt.setDate(dt.getDate() + d);
  return dt.toISOString().split('T')[0];
};

// Genereer consistente PDF-bestandsnaam in juiste taal: Factuur/Facture/Invoice/Rechnung_FAC-2026-0177_KLANTNAAM.pdf
const TYPE_LABELS_PDF_PER_LANG = {
  nl: { factuur: 'Factuur', creditnota: 'Creditnota', offerte: 'Offerte', bestelbon: 'Bestelbon', leverbon: 'Leveringsbon', proforma: 'Proforma' },
  fr: { factuur: 'Facture', creditnota: 'NoteCredit', offerte: 'Devis', bestelbon: 'BonCommande', leverbon: 'BonLivraison', proforma: 'ProForma' },
  en: { factuur: 'Invoice', creditnota: 'CreditNote', offerte: 'Quote', bestelbon: 'Order', leverbon: 'DeliveryNote', proforma: 'ProForma' },
  de: { factuur: 'Rechnung', creditnota: 'Gutschrift', offerte: 'Angebot', bestelbon: 'Bestellung', leverbon: 'Lieferschein', proforma: 'ProformaRechnung' },
};
export const pdfFilename = (doc, klant) => {
  const lang = (doc?.taal || klant?.taal || 'nl').toLowerCase();
  const labels = TYPE_LABELS_PDF_PER_LANG[lang] || TYPE_LABELS_PDF_PER_LANG.nl;
  const type = labels[doc.type] || 'Document';
  const nummer = (doc.id || '').toString();
  const klantRaw = (klant?.bedrijf || `${klant?.voorn || ''} ${klant?.achtern || ''}`).toString().trim();
  // Normaliseer: verwijder accenten, spaties, interpunctie, alleen A-Z 0-9
  const klantClean = klantRaw
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 30);
  const parts = [type, nummer, klantClean].filter(Boolean);
  return parts.join('_') + '.pdf';
};
